import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import Navbar from "../components/Navbar";
import Footer1 from "../components/Footer1";
import axios from "axios";
import { motion } from "framer-motion";
import "./SeriesDetails.css"; // You can reuse the same CSS file since the layout is similar

// =========================================================
// 1. INDIVIDUAL PAINTING COMPONENT
// =========================================================
const CategoryItem = ({ painting, wishlist, handleAddToWishlist }) => {
    const navigate = useNavigate();

    // --- LOCAL STATE ---
    const [paintingIndex, setPaintingIndex] = useState(0);
    const [zoomStyle, setZoomStyle] = useState({
        backgroundPosition: "center",
        backgroundSize: "contain",
        cursor: "zoom-in"
    });
    const [readMore, setReadMore] = useState(false);
    const [addingToCart, setAddingToCart] = useState(false);

    // --- Helpers ---
    const formatName = (name) =>
        name?.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

    // --- Zoom Logic ---
    const handleMouseMove = (e) => {
        const { left, top, width, height } = e.target.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;
        setZoomStyle({
            backgroundPosition: `${x}% ${y}%`,
            backgroundSize: "200%", 
            cursor: "crosshair"
        });
    };

    const handleMouseLeave = () => {
        setZoomStyle({
            backgroundPosition: "center",
            backgroundSize: "contain",
            cursor: "zoom-in"
        });
    };

    // --- Add To Cart (Single Item) ---
    const handleAddToCart = async () => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            alert("Please login to add items to your cart 🛒");
            navigate("/signUp");
            return;
        }
        
        setAddingToCart(true);
        try {
            await axios.post(
                `http://localhost:8080/api/cart/add/${painting.id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(`${painting.title} added to cart successfully 🛒`);
        } catch (err) {
            if (err.response?.status === 409) alert("This painting is already in your cart!");
            else alert(err.response?.data?.message || "Something went wrong.");
        } finally {
            setAddingToCart(false);
        }
    };

    return (
        <div className="painting_container" style={{ marginBottom: "120px", borderBottom: "1px solid #eee", paddingBottom: "60px" }}>
            {/* Left - Painting images */}
            <div className="big_image">
                <div
                    className="painting_img"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{
                        backgroundImage: `url(${painting?.images[paintingIndex]?.imageUrl})`,
                        backgroundPosition: zoomStyle.backgroundPosition,
                        backgroundSize: zoomStyle.backgroundSize,
                        cursor: zoomStyle.cursor,
                        transition: "background-size 0.2s ease-out"
                    }}
                ></div>
                <div className="painting_options">
                    {painting.images?.slice(0, 4).map((img, idx) => (
                        <div
                            key={idx}
                            className="painting_option1"
                            style={{
                                backgroundImage: `url(${img.imageUrl})`,
                                borderColor: `${paintingIndex === idx ? "black" : "#ccc"}`,
                            }}
                            onClick={() => setPaintingIndex(idx)}
                        ></div>
                    ))}
                </div>
            </div>

            {/* Right - Painting details */}
            <motion.div
                className="details-right"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <span className="artist-label">By {formatName(painting.artist)}</span>
                <h1 className="art-title">{painting.title}</h1>

                <div className="specs-row">
                    <span>{painting.size || "Size N/A"}</span>
                    <span className="separator">•</span>
                    <span>{painting.material || "Mixed Media"}</span>
                    <span className="separator">•</span>
                    <span>Original</span>
                </div>

                <p className="art-price">₹ {painting.price?.toLocaleString("en-IN")}</p>

                {/* ✅ ADD TO CART & WISHLIST BUTTONS */}
                <div className="action-buttons-group" style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                    <button 
                        className="btn-buy-series" 
                        style={{ flex: 1, padding: "15px", backgroundColor: "#000", color: "#fff", border: "none", cursor: "pointer" }}
                        onClick={handleAddToCart}
                        disabled={painting.sold || addingToCart}
                    >
                        {painting.sold ? "Sold Out" : addingToCart ? "Adding..." : "Add to Cart"}
                    </button>

                    <button
                        className={`btn-wishlist-main ${wishlist.includes(painting.id) ? 'active' : ''}`}
                        style={{ padding: "15px 20px", border: "1px solid #ddd", backgroundColor: "#fff", cursor: "pointer", fontSize: "1.2rem" }}
                        onClick={() => handleAddToWishlist(painting.id)}
                    >
                        <i className={`fa-${wishlist.includes(painting.id) ? 'solid' : 'regular'} fa-heart`} style={{ color: wishlist.includes(painting.id) ? "red" : "black" }}></i>
                    </button>
                </div>

                <div className="art-story" style={{ marginTop: "30px" }}>
                    <h3>The Narrative</h3>
                    <p style={{ whiteSpace: "pre-wrap" }}>
                        {readMore ? painting?.story : `${painting?.story?.substring(0, 200) || "No narrative available for this artwork."}...`}
                        {painting?.story?.length > 200 && (
                            <span 
                                className="read-more-link" 
                                onClick={() => setReadMore(!readMore)}
                                style={{ cursor: "pointer", textDecoration: "underline", fontWeight: "bold", marginLeft: "5px" }}
                            >
                                {readMore ? " Read Less" : " Read More"}
                            </span>
                        )}
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

// =========================================================
// 2. PARENT CATEGORY PAGE
// =========================================================
const CategoryDetails = () => {
    // We grab the category name from the URL (e.g., /category/Innovations)
    const { categoryName } = useParams(); 
    const [paintings, setPaintings] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem("authToken");
    const navigate = useNavigate();

    // Animation
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    useEffect(() => {
        fetchCategoryPaintings();
        fetchWishlist();
        window.scrollTo(0, 0);
    }, [categoryName]);

    const fetchCategoryPaintings = async () => {
        try {
            const res = await axios.get("http://localhost:8080/api/paintings/all");
            
            // Filter paintings where the category matches the URL parameter
            const decodedCategory = decodeURIComponent(categoryName).toLowerCase();
            const categoryData = res.data.filter(
                (p) => p.category && p.category.toLowerCase() === decodedCategory
            );
            
            setPaintings(categoryData);
        } catch (err) {
            console.error("Error fetching category:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchWishlist = async () => {
        if (!token) return;
        try {
            const res = await axios.get("http://localhost:8080/api/wishlist", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWishlist(res.data.map((item) => item.painting.id));
        } catch (err) {
            console.error("Error fetching wishlist:", err);
        }
    };

    const handleAddToWishlist = async (paintingId) => {
        if (!token) {
            alert("Please log in to add items to wishlist.");
            navigate("/signUp");
            return;
        }
        try {
            await axios.post(`http://localhost:8080/api/wishlist/add/${paintingId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWishlist((prev) =>
                prev.includes(paintingId) ? prev.filter((id) => id !== paintingId) : [...prev, paintingId]
            );
        } catch (err) {
            alert("Failed to update wishlist.");
        }
    };

    if (loading) return <div className="loading-div">Loading Collection...</div>;

    if (paintings.length === 0) {
        return (
            <div className="series-not-found" style={{ textAlign: "center", padding: "100px 20px" }}>
                <h2>No Paintings Found</h2>
                <p>We couldn't find any artwork in the "{decodeURIComponent(categoryName)}" category.</p>
                <button onClick={() => navigate("/")} style={{ padding: "10px 20px", marginTop: "20px", cursor: "pointer" }}>Go Home</button>
            </div>
        );
    }

    return (
        <>
            {/* <Navbar /> */}
            <div className="series-details-page">

                {/* --- HEADER --- */}
                <motion.div
                    className="series-header-section"
                    initial="hidden"
                    animate="visible"
                    variants={fadeInUp}
                    style={{ textAlign: "center", padding: "60px 20px", backgroundColor: "#f9f9f9" }}
                >
                    <span className="series-label">Curated Category</span>
                    <h1 className="series-main-title">{decodeURIComponent(categoryName)}</h1>
                    <p className="series-description" style={{ maxWidth: "600px", margin: "10px auto", color: "#666" }}>
                        Explore our exclusive collection of {paintings.length} masterworks in this category. 
                        Each piece is available for individual acquisition.
                    </p>
                </motion.div>

                {/* --- LIST OF PAINTINGS --- */}
                <div className="series-items-list" style={{ marginTop: "50px", padding: "0 5%" }}>
                    {paintings.map((painting) => (
                        <CategoryItem
                            key={painting.id}
                            painting={painting}
                            wishlist={wishlist}
                            handleAddToWishlist={handleAddToWishlist}
                        />
                    ))}
                </div>

            </div>
            <Footer1 />
        </>
    );
};

export default CategoryDetails;